import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontFamilies, fontSizes, fontWeights, headings, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

// The `WidgetProps` interface provides a lot of information,
// including the CMS field values. The props object for a widget
// should always extend this interface.

interface OwnProps extends WidgetProps {
    // The values of the CMS fields are passed to the widget
    // in a `Dictionary` named "fields". You can statically type
    // the names of the fields to make access easier. This also
    // allows the TypeScript compiler to give you type-checking.
    fields: {
        image: string;
        imageAltText: string;
        heading: string;
        subText: string;
        description: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
    };
}


export interface ImageCalloutStyles {
    wrapper?: InjectableCss;
    image?: ImgProps;
    content?: InjectableCss;
    heading?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    buttonWrapper?: ButtonPresentationProps;
    buttonText?: TypographyPresentationProps;
}

export const imageCalloutStyles: ImageCalloutStyles = {
    wrapper: {
        css: css`
            display: flex;
            max-width: 557px;
            width: 100%;
            flex-direction: column;
            align-items: flex-start;
            gap: 32px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                        `,
                        null,
                        css`
                            max-width: 100%;
                            gap: 24px;
                            flex-shrink: 0;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                        padding: 40px 16px;                   
                    `,
                        css`                            
                       padding: 64px 24px;        
                    `,
                        null,
                        css`
                       padding: 64px 24px;                    
                    `,
                        css`                           
                       padding: 80px 40px;                            
                    `
                    ],
                    "min",
                )} 
        `,
    },
    content: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            width: 100%;
        `,
    },
    image: {
        css: css`
        width: 100%;
        max-width: 300px;
        max-height: 300px;
        aspect-ratio: 1/1;
    `,
    },
    heading: {
        css: css`
        color: ${themeColors.primary.richBlack};
        font-family: ${fontFamilies.inter};
        font-size: ${headings.h2.fontSize};
        font-style: normal;
        font-weight: ${headings.h2.fontWeight};
        line-height: ${headings.h2.lineHeight}; 
        letter-spacing: -0.68px;
        align-self: stretch;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headings.h2.fontSizeSmall};
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    subText: {
        css: css`
        color: ${themeColors.primary.richBlack};
        font-family: ${fontFamilies.inter};
        font-size: ${headings.h3.fontSize};
        font-style: normal;
        font-weight: ${headings.h3.fontWeight};
        line-height: ${headings.h3.lineHeight}; 
        letter-spacing: -0.68px;
        align-self: stretch;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            ${headings.h3.fontSizeSmall};
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    description: {
        css: css`
        color: ${themeColors.primary.richBlack};
        font-family: ${fontFamilies.inter};
        font-size: ${fontSizes.medium};
        font-style: normal;
        font-weight: ${fontWeights.regular};
        line-height: ${lineHeights.medium};
        letter-spacing: -0.68px;
        align-self: stretch;
    `,
    },
    buttonWrapper: {
        css: css`
        align-items: center;
        gap: 10px;
    `,
    },
    buttonText: {
        css: css`
        color: ${themeColors.primary.danaBlue};
        font-family: ${themeColors.primary.richBlack};
        font-size: ${fontSizes.medium};
        font-style: normal;
        font-weight: ${fontWeights.medium};
        line-height: ${lineHeights.medium}; 
    `,
    },
};
const styles = imageCalloutStyles;
const DanaImageCallout = ({ fields }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLink = useGetLink(fields.buttonLink);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper} className="wrapper">
            {fields.image && (
                <Img src={fields.image} altText={fields.imageAltText || ''} {...styles.image} />
            )}
            <StyledWrapper {...styles.content} className="content">
                {fields.heading && (<Typography {...styles.heading} as="h2">{fields.heading}</Typography>)}
                {fields.subText && (<Typography {...styles.subText} as="h3">{fields.subText}</Typography>)}
                {fields.description && (
                    <Typography {...styles.description}>
                        {parse(fields.description, parserOptions)}
                    </Typography>
                )}
            </StyledWrapper>
            {(fields.buttonLabel || fields.buttonLink.value) && (
                <Button
                    {...styles.buttonWrapper}
                    variant={fields.buttonVariant}
                    onClick={() => onClick(buttonLink?.url)}
                >
                    {fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                </Button>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: DanaImageCallout,
    definition: {
        group: "Common",
        displayName: "Image Callout",
        fieldDefinitions: [
            {
                name: "image",
                displayName: "Image",
                fieldType: "Translatable",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: "imageAltText",
                displayName: "Image Alt Text",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: "heading",
                displayName: "Heading",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: "subText",
                displayName: "Subtext",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: "description",
                displayName: "Description",
                fieldType: "Translatable",
                editorTemplate: "RichTextField",
                defaultValue: "",
                isRequired: false,
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "specialCharacters",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                name: "buttonLabel",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                name: "buttonLink",
                fieldType: "Translatable",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: "buttonVariant",
                fieldType: "Translatable",
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "secondary",
            },
        ],
    },
};

export default widgetModule;